import React, { useState, useEffect } from 'react';
import { useClinic } from '@/contexts/ClinicContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import WebhookList from '@/components/clinic/webhooks/WebhookList';
import WebhookForm from '@/components/clinic/webhooks/WebhookForm';
import DeleteWebhookDialog from '@/components/clinic/webhooks/DeleteWebhookDialog';
import {
  webhookService,
  Webhook,
  CreateWebhookRequest,
} from '@/services/webhookService';

const WebhooksPage: React.FC = () => {
  const { clinic, isAdmin } = useClinic();
  const navigate = useNavigate();
  const location = useLocation();
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<Webhook | null>(null);
  const [deletingWebhook, setDeletingWebhook] = useState<Webhook | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get base path based on current portal
  const getBasePath = () => {
    if (location.pathname.startsWith('/practitioner-portal')) {
      return '/practitioner-portal';
    }
    // Admin + clinic_staff both live at root.
    return '';
  };

  const basePath = getBasePath();

  useEffect(() => {
    if (!isAdmin) {
      // Redirect non-admin users
      navigate(`${basePath}/settings`);
      return;
    }

    if (clinic?.id) {
      loadWebhooks();
    }
  }, [clinic?.id, isAdmin, navigate]);

  const loadWebhooks = async () => {
    if (!clinic?.id) return;

    try {
      setLoading(true);
      const data = await webhookService.listWebhooks(clinic.id);
      setWebhooks(data);
    } catch (error: any) {
      console.error('Failed to load webhooks:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load webhooks',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingWebhook(null);
    setIsFormOpen(true);
  };

  const handleEdit = (webhook: Webhook) => {
    setEditingWebhook(webhook);
    setIsFormOpen(true);
  };

  const handleDelete = (webhook: Webhook) => {
    setDeletingWebhook(webhook);
    setIsDeleteDialogOpen(true);
  };

  const handleView = (webhook: Webhook) => {
    navigate(`${basePath}/settings/webhooks/${webhook.id}`);
  };

  const handleFormSubmit = async (data: CreateWebhookRequest) => {
    if (!clinic?.id) return;

    try {
      setIsSubmitting(true);
      if (editingWebhook) {
        await webhookService.updateWebhook(clinic.id, editingWebhook.id, data);
        toast({
          title: 'Webhook Updated',
          description: 'Webhook has been updated successfully',
          variant: 'success',
        });
      } else {
        await webhookService.createWebhook(clinic.id, data);
        toast({
          title: 'Webhook Created',
          description: 'Webhook has been created successfully',
          variant: 'success',
        });
      }
      setIsFormOpen(false);
      setEditingWebhook(null);
      await loadWebhooks();
    } catch (error: any) {
      console.error('Failed to save webhook:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save webhook',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!clinic?.id || !deletingWebhook) return;

    try {
      await webhookService.deleteWebhook(clinic.id, deletingWebhook.id);
      toast({
        title: 'Webhook Deleted',
        description: 'Webhook has been deleted successfully',
        variant: 'success',
      });
      setIsDeleteDialogOpen(false);
      setDeletingWebhook(null);
      await loadWebhooks();
    } catch (error: any) {
      console.error('Failed to delete webhook:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete webhook',
        variant: 'destructive',
      });
    }
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingWebhook(null);
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="container mx-auto py-6 px-4 max-w-6xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="mb-2 flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="h-9 w-9">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                Webhook Management
              </h1>
            </div>
            <p className="text-muted-foreground mt-1">
              Configure webhooks to receive real-time event notifications from NovaMed
            </p>
          </div>
          <Button onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Create Webhook
          </Button>
        </div>
      </div>

      {/* Webhook List */}
      <WebhookList
        webhooks={webhooks}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onView={handleView}
      />

      {/* Create/Edit Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingWebhook ? 'Edit Webhook' : 'Create Webhook'}
            </DialogTitle>
            <DialogDescription>
              {editingWebhook
                ? 'Update webhook configuration and settings'
                : 'Configure a new webhook to receive event notifications'}
            </DialogDescription>
          </DialogHeader>
          <WebhookForm
            webhook={editingWebhook}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
            isLoading={isSubmitting}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteWebhookDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        webhook={deletingWebhook}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default WebhooksPage;

